import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "lucide-react";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const loadCount = () => api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
  const loadList = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    loadList();
  };

  const clickItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); loadCount(); }
    setOpen(false);
    if (n.link) navigate(n.link);
    else if (n.prospect_id) navigate(`/prospects/${n.prospect_id}`);
  };

  return (
    <div className="fixed top-4 right-4 z-40" ref={ref}>
      <button
        onClick={toggle}
        data-testid="notification-bell"
        className="relative w-11 h-11 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:border-[#B8873A] transition-colors"
      >
        <Bell className="w-5 h-5 text-[#111111]" strokeWidth={1.75} />
        {count > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B8873A] text-white text-[10px] font-bold flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-[#111111]">Notifications</span>
            {items.some((n) => !n.read) && (
              <button onClick={markAll} className="text-xs text-[#B8873A] hover:underline flex items-center gap-1" data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Tout marquer lu
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">Aucune notification</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => clickItem(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${n.read ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#B8873A] mt-1.5 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
